/* StudentSkills — practice list + practice runner for students.
 * Lists available skills from all their teachers, filterable by subject/
 * difficulty. Clicking a skill starts a step-by-step question loop with
 * instant validation, running score, and progression to the next question.
 * On completion the attempt is saved via /api/skills/{id}/attempt.
 */
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Filter, ChevronRight, Sparkles, CheckCircle2, XCircle, RotateCcw, Trophy, ChevronLeft } from "lucide-react";
import api from "@/lib/api";

const DIFFICULTIES = ["easy", "medium", "hard"];

export default function StudentSkills() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [active, setActive] = useState(null); // skill being practised

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/skills", { params: { q, difficulty } });
        setSkills(data.skills || []);
      } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
      finally { setLoading(false); }
    })();
  }, [q, difficulty]);

  if (active) {
    return <PracticeRunner skill={active} onClose={() => setActive(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="student-skills-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ practice</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">Skills</h1>
        <p className="text-sm text-neutral-600 mt-1 max-w-2xl">Pick a skill and get one question at a time — instant feedback, running score, no time pressure.</p>
      </div>

      <div className="bg-white border border-black/8 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Search size={14} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search topic, title…"
            data-testid="student-skills-search" className="flex-1 bg-transparent border-none outline-none text-sm" />
        </div>
        <div className="flex items-center gap-2">
          {["", ...DIFFICULTIES].map((d) => (
            <button key={d || "all"} onClick={() => setDifficulty(d)} data-testid={`student-skills-diff-${d || "all"}`}
              className={`text-[10px] px-2.5 py-1 rounded-sm border capitalize ${difficulty === d ? "bg-[#0A0C10] text-white border-[#0A0C10]" : "bg-white text-neutral-600 border-black/10 hover:border-black/30"}`}>
              {d || "all"}
            </button>
          ))}
          <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-neutral-400 inline-flex items-center gap-1">
            <Filter size={10} /> {skills.length} skill{skills.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-500 text-sm py-12">Loading…</div>
      ) : skills.length === 0 ? (
        <div className="text-center text-neutral-500 text-sm py-16 bg-white border border-black/8 rounded-xl">Your teacher hasn&rsquo;t added any skills yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {skills.map((s) => {
            const qCount = (s.payload?.questions || []).length;
            return (
              <button key={s.id} onClick={() => qCount > 0 ? setActive(s) : toast.error("No questions in this skill yet")}
                data-testid={`student-skill-${s.id}`}
                className="text-left bg-white border border-black/8 rounded-xl p-4 hover:border-[#7C3AED] transition-colors group">
                <div className="w-8 h-8 rounded-md bg-[#7C3AED]/15 text-[#7C3AED] flex items-center justify-center">
                  <Sparkles size={14} />
                </div>
                <div className="mt-3 font-display font-semibold text-[15px] text-[#0A0C10]">{s.title}</div>
                <div className="mt-1 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
                  {s.difficulty} · {qCount} question{qCount === 1 ? "" : "s"}
                </div>
                {s.description && <div className="mt-2 text-xs text-neutral-600 line-clamp-3">{s.description}</div>}
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[#7C3AED] group-hover:gap-2 transition-all">
                  Start practice <ChevronRight size={12} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PracticeRunner({ skill, onClose }) {
  const questions = useMemo(() => skill.payload?.questions || [], [skill]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [done, setDone] = useState(false);
  const [saved, setSaved] = useState(false);
  const startRef = React.useRef(Date.now());

  const q = questions[idx];
  const correctIdx = q?.answer_index ?? 0;
  const isCorrect = picked === correctIdx;
  const score = answers.filter((a) => a.correct).length;

  const submit = () => {
    if (picked === null) return;
    setShowFeedback(true);
    setAnswers((a) => [...a, { qIdx: idx, picked, correct: isCorrect }]);
  };
  const next = async () => {
    setShowFeedback(false);
    setPicked(null);
    if (idx + 1 >= questions.length) {
      setDone(true);
      // Persist attempt
      try {
        const duration = Math.round((Date.now() - startRef.current) / 1000);
        const finalScore = answers.filter((a) => a.correct).length + (isCorrect ? 1 : 0);
        await api.post(`/skills/${skill.id}/attempt`, {
          answers: [...answers, { qIdx: idx, picked, correct: isCorrect }],
          score: finalScore, total: questions.length, duration_s: duration,
        });
        setSaved(true);
      } catch { /* silent */ }
    } else {
      setIdx((i) => i + 1);
    }
  };

  if (done) {
    const finalScore = answers.filter((a) => a.correct).length;
    const pct = Math.round((finalScore / questions.length) * 100);
    return (
      <div className="max-w-2xl mx-auto py-8 space-y-6 text-center" data-testid="practice-done">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#FFD600]/20 flex items-center justify-center">
          <Trophy size={28} className="text-[#B45309]" />
        </div>
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ practice complete</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter mt-1">{finalScore} / {questions.length}</h1>
          <div className="text-lg font-semibold mt-2 text-neutral-600">{pct}% correct</div>
          {saved && <div className="text-[11px] font-mono uppercase tracking-wider text-emerald-600 mt-3 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Attempt saved</div>}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => { setIdx(0); setAnswers([]); setPicked(null); setDone(false); setSaved(false); startRef.current = Date.now(); }}
            data-testid="practice-retry"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-black/15 hover:border-[#0A0C10] text-sm font-semibold">
            <RotateCcw size={13} /> Retry
          </button>
          <button onClick={onClose} data-testid="practice-back"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A0C10] text-[#FFD600] text-sm font-bold">
            <ChevronLeft size={13} /> Back to skills
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-4 space-y-4" data-testid="practice-runner">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-neutral-500 hover:text-[#0A0C10] inline-flex items-center gap-1" data-testid="practice-exit">
          <ChevronLeft size={12} /> Back
        </button>
        <div className="text-xs font-mono uppercase tracking-wider text-neutral-500">
          Q{idx + 1} / {questions.length} · score {score}
        </div>
      </div>
      <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
        <div className="h-full bg-[#7C3AED] transition-all" style={{ width: `${((idx) / questions.length) * 100}%` }} />
      </div>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#7C3AED]">/ {skill.title}</div>
        <h2 className="font-display font-bold text-2xl tracking-tighter mt-1">{q?.prompt}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="practice-options">
        {(q?.options || []).map((opt, k) => {
          const isPicked = picked === k;
          const feedbackTint =
            !showFeedback ? (isPicked ? "border-[#7C3AED] bg-[#7C3AED]/8" : "border-black/15 hover:border-black/40")
            : k === correctIdx ? "border-emerald-500 bg-emerald-50"
            : isPicked ? "border-red-500 bg-red-50"
            : "border-black/15 opacity-60";
          return (
            <button key={k} onClick={() => !showFeedback && setPicked(k)}
              data-testid={`practice-option-${k}`}
              disabled={showFeedback || !opt.trim()}
              className={`text-left bg-white border rounded-md px-3 py-3 text-sm transition-colors ${feedbackTint} ${!opt.trim() ? "opacity-40 cursor-not-allowed" : ""}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{String.fromCharCode(65 + k)}</div>
              <div className="mt-0.5 font-medium">{opt || <em className="opacity-40">—</em>}</div>
              {showFeedback && k === correctIdx && <div className="mt-1 text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Correct</div>}
              {showFeedback && k === picked && k !== correctIdx && <div className="mt-1 text-[11px] font-semibold text-red-600 inline-flex items-center gap-1"><XCircle size={11} /> Not quite</div>}
            </button>
          );
        })}
      </div>
      <AnimatePresence>
        {showFeedback && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`p-4 rounded-md border ${isCorrect ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
            <div className={`text-sm font-semibold ${isCorrect ? "text-emerald-700" : "text-red-700"} inline-flex items-center gap-1.5`}>
              {isCorrect ? <><CheckCircle2 size={14} /> Nailed it</> : <><XCircle size={14} /> The right answer was <strong>{String.fromCharCode(65 + correctIdx)}</strong></>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex items-center justify-end">
        {!showFeedback ? (
          <button onClick={submit} disabled={picked === null} data-testid="practice-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A0C10] text-[#FFD600] text-sm font-bold disabled:opacity-40">
            Check
          </button>
        ) : (
          <button onClick={next} data-testid="practice-next"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#7C3AED] text-white text-sm font-bold">
            {idx + 1 >= questions.length ? "Finish" : "Next"} <ChevronRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
